/**
 * The idle lock — `docs/05-admin/02` §2.3.
 *
 * Twelve hours without pointer or key activity locks the console (owner, 2026-10-30: an admin
 * session lasts twelve hours and nothing signs it out sooner; it was thirty minutes). It then
 * sits behind an overlay that needs the passphrase, and a code if the admin enrolled one, to
 * lift. It is **UX hardening for a shared ops desk**, not a security control: the server-side truth is token expiry, rotation and revocation, all of which happen
 * whether or not this timer runs. What it buys is that a walked-away-from console in a room full
 * of people stops showing devotee phone numbers.
 *
 * Activity is throttled to one reset per `RESET_THROTTLE_MS`, because the alternative is resetting
 * a timer on every mousemove, and this file would then be the console's largest CPU cost.
 */
export const IDLE_LIMIT_MS = 12 * 60 * 60 * 1000;
const RESET_THROTTLE_MS = 5_000;
const ACTIVITY = ['pointerdown', 'keydown', 'wheel'] as const;

/**
 * Calls `onIdle` once, after `limitMs` of no activity. Returns its own unsubscribe.
 *
 * `now` is injected so the tests can drive the clock rather than wait twelve hours for it.
 */
export function watchIdle(onIdle: () => void, limitMs = IDLE_LIMIT_MS, now: () => number = Date.now): () => void {
  let last = now();
  let fired = false;

  const timer = setInterval(() => {
    if (!fired && now() - last >= limitMs) {
      fired = true;
      onIdle();
    }
  }, 1_000);

  const bump = () => {
    const t = now();
    /* Once locked, activity does not unlock — the overlay does. Otherwise the first click of
       whoever sat down at the desk would dismiss the lock they were meant to face. */
    if (fired || t - last < RESET_THROTTLE_MS) return;
    last = t;
  };

  for (const event of ACTIVITY) window.addEventListener(event, bump, { passive: true });

  return () => {
    clearInterval(timer);
    for (const event of ACTIVITY) window.removeEventListener(event, bump);
  };
}
